import React from "react";
import { Page, Text, View, Document, StyleSheet, Image, PDFViewer } from "@react-pdf/renderer";
import { dateToFullString, dateToString } from "utils/dateUtils";
import { hasElements } from "utils/arrays";
import { Tenant } from "utils/tenants";

const statuses = [
    {
        value: "casePreparation",
        label: "Case Preparation",
        style: {
            backgroundColor: "#eaecf7",
            color: "#3f51b5",
        },
    },
    {
        value: "gettingAttorneyReview",
        label: "Getting Attorney Review",
        style: {
            backgroundColor: "#fff3cd",
            color: "#856404",
        },
    },
    {
        value: "approved",
        label: "Approved",
        style: {
            backgroundColor: "#eaf7eb",
            color: "#155724",
        },
    },
    {
        value: "completed",
        label: "Completed",
        style: {
            backgroundColor: "#eaf7eb",
            color: "#155724",
        },
    },
    {
        value: "rejected",
        label: "Rejected",
        style: {
            backgroundColor: "#f8d7da",
            color: "#721c24",
        },
    },
];

const statusStyles = statuses.reduce((map, item) => {
    return {
        ...map,
        [`statusBar_${item.value}`]: {
            flexDirection: "row",
            alignContent: "flex-start",
            width: "100%",
            padding: 5,
            maxHeight: 40,
            fontSize: 10,
            ...item.style,
        },
    };
}, {});

const styles = StyleSheet.create({
    page: {
        paddingTop: 20,
        paddingBottom: 65,
        paddingHorizontal: 10,
    },
    divider: {
        width: "100%",
        height: 1,
        backgroundColor: "black",
        marginVertical: 20,
    },
    header: {
        flexDirection: "row",
        marginVertical: 10,
        justifyContent: "space-between",
        alignItems: "center",
    },
    logo: {
        height: 64,
        width: 87,
    },
    title: {
        fontSize: 18,
        fontWeight: "bold",
        textAlign: "center",
        width: "100%",
    },
    pageNumber: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 10,
        right: 10,
        textAlign: "right",
        color: "grey",
    },
    time: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 10,
        right: 10,
        textAlign: "left",
        color: "grey",
    },
    printedBy: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 10,
        right: 10,
        textAlign: "center",
        color: "grey",
    },
    footer: {
        position: "absolute",
        bottom: 40,
        left: 10,
        right: 10,
    },
    detail: {
        width: "100%",
    },
    tableHead: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 10,
        backgroundColor: "silver",
        width: "100%",
        height: 20,
    },
    columnHeader: {
        fontSize: 10,
        fontWeight: "700",
        color: "black",
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 10,
    },
    tableBody: {
        borderWidth: 1,
        borderColor: "silver",
    },
    row: {
        flexDirection: "row",
        alignContent: "flex-start",
        padding: 2,
        borderBottomWidth: 1,
        borderBottomColor: "silver",
    },
    oddRow: {
        backgroundColor: "whitesmoke",
    },
    cell: {
        fontSize: 8,
        paddingHorizontal: 2,
        paddingVertical: 4,
        flex: 1,
        textAlign: "left",
        marginLeft: 10,
    },
    ...statusStyles,
    alertIcon_aboutToExpire: {
        color: "#ffcc00",
        width: 16,
        height: 16,
    },
    alertIcon_expired: {
        color: "#ff3333",
        width: 16,
        height: 16,
    },
});

const MyDocument = ({ data, currentUser }) => {
    const groupedData = statuses.reduce((map, status) => ({ ...map, [status.value]: data.filter((x) => x.workStatus === status.value) }), {});

    return (
        <PDFViewer width="100%" height="900px">
            <Document>
                <Page size="A4" style={styles.page}>
                    <View style={styles.header} fixed>
                        <Text style={styles.title}>CASE ASSIGNMENTS REPORT</Text>
                        <Image style={styles.logo} src={Tenant.getLogo()} />
                    </View>

                    <View style={styles.divider} fixed />

                    <View style={styles.detail}>
                        <View style={styles.tableHead} fixed>
                            <View style={{ ...styles.columnHeader, flexBasis: "15%" }}>
                                <Text>Assignee</Text>
                            </View>
                            <View style={{ ...styles.columnHeader, flexBasis: "25%" }}>
                                <Text>Task</Text>
                            </View>
                            <View style={{ ...styles.columnHeader, flexBasis: "15%" }}>
                                <Text>Due Date</Text>
                            </View>
                        </View>
                        {statuses.map((status) => {
                            const statusData = groupedData[status.value];

                            return (
                                hasElements(statusData) && (
                                    <View style={styles.tableBody}>
                                        <View key={`status-bar-${status.value}`} style={styles[`statusBar_${status.value}`]}>
                                            <Text>{status.label.toUpperCase()}</Text>
                                        </View>
                                        {statusData.map((assignment, index) => {
                                            return (
                                                <View key={index} style={{ ...styles.row, ...(index % 2 === 1 ? styles.oddRow : {}) }}>
                                                    <View style={{ ...styles.cell, flexBasis: "15%" }}>
                                                        <Text>{assignment.assigneeUser?.name || "--"}</Text>
                                                    </View>
                                                    <View style={{ ...styles.cell, flexBasis: "25%" }}>
                                                        <Text>{assignment.fileNo}</Text>
                                                    </View>
                                                    <View style={{ ...styles.cell, flexBasis: "15%" }}>
                                                        <Text>{assignment.dueDate ? dateToString(assignment.dueDate, false) : "--"}</Text>
                                                    </View>
                                                </View>
                                            );
                                        })}
                                    </View>
                                )
                            );
                        })}
                    </View>

                    <View style={styles.footer}>
                        <View style={styles.divider} />
                    </View>

                    <Text style={styles.time} render={() => dateToFullString(new Date())} fixed />

                    <Text style={styles.printedBy} render={() => `Printed by ${currentUser.name}`} fixed />

                    <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
                </Page>
            </Document>
        </PDFViewer>
    );
};

export default MyDocument;
